import React, { useState } from 'react';
import { Check, Copy, MapPin, Pencil, Phone, Trash2 } from 'lucide-react';
import type { Passenger } from '../../../models/PassengerModel';
import CompanyCell from '../../../components/Base/CompanyCell';
import { isPlaceholderAddress } from '../../../components/Passengers/bulkPassengerValidation';
import './PassengerCard.css';

export type PassengerCardProps = {
  passenger: Passenger;
  onEdit?: (passenger: Passenger) => void;
  onDelete?: (id: number) => void;
};

function getInitials(name?: string, first?: string): string {
  const a = (name ?? '').trim().charAt(0);
  const b = (first ?? '').trim().charAt(0);
  return `${a}${b}`.toUpperCase() || '?';
}

function fullName(p: Passenger): string {
  return [p.name, p.first_lastname, p.second_lastname]
    .map((x) => (x ?? '').trim())
    .filter(Boolean)
    .join(' ');
}

function getAddresses(p: Passenger): string[] {
  return (p.addresses ?? [])
    .map((a) => (a.street ?? '').trim())
    .filter((street) => street && !isPlaceholderAddress(street));
}

async function copyText(value: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(value);
      return true;
    }
  } catch {
    /* fallback below */
  }
  try {
    const el = document.createElement('textarea');
    el.value = value;
    el.setAttribute('readonly', '');
    el.style.position = 'fixed';
    el.style.left = '-9999px';
    document.body.appendChild(el);
    el.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(el);
    return ok;
  } catch {
    return false;
  }
}

const PassengerCard: React.FC<PassengerCardProps> = ({
  passenger,
  onEdit,
  onDelete,
}) => {
  const name = fullName(passenger) || 'Sin nombre';
  const phone = passenger.phone?.trim() || '';
  const addresses = getAddresses(passenger);
  const hasActions = Boolean(onEdit || onDelete);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  const handleCopy = async (key: string, value: string) => {
    const ok = await copyText(value);
    if (!ok) return;
    setCopiedKey(key);
    window.setTimeout(() => {
      setCopiedKey((current) => (current === key ? null : current));
    }, 1200);
  };

  return (
    <article className="passenger-card" aria-label={`Pasajero ${name}`}>
      {hasActions ? (
        <div className="passenger-card__actions">
          {onEdit ? (
            <button
              type="button"
              className="passenger-card__icon-btn passenger-card__icon-btn--edit"
              onClick={() => onEdit(passenger)}
              title="Editar"
              aria-label={`Editar ${name}`}
            >
              <Pencil size={15} strokeWidth={2} aria-hidden />
            </button>
          ) : null}
          {onDelete && passenger.id != null ? (
            <button
              type="button"
              className="passenger-card__icon-btn passenger-card__icon-btn--danger"
              onClick={() => onDelete(Number(passenger.id))}
              title="Eliminar"
              aria-label={`Eliminar ${name}`}
            >
              <Trash2 size={15} strokeWidth={2} aria-hidden />
            </button>
          ) : null}
        </div>
      ) : null}

      <header className="passenger-card__header">
        <div className="passenger-card__avatar" aria-hidden>
          <span className="passenger-card__avatar-fallback">
            {getInitials(passenger.name, passenger.first_lastname)}
          </span>
        </div>
        <div className="passenger-card__identity">
          <h3 className="passenger-card__name" title={name}>
            {name}
          </h3>
          <div className="passenger-card__company">
            <CompanyCell name={passenger.company_name} />
          </div>
        </div>
      </header>

      <div className="passenger-card__meta">
        <div className="passenger-card__row">
          <p className="passenger-card__phone">
            <Phone size={14} strokeWidth={2} aria-hidden />
            <span>{phone || 'Sin celular'}</span>
          </p>
          {phone ? (
            <button
              type="button"
              className={`passenger-card__copy-btn${copiedKey === 'phone' ? ' is-copied' : ''}`}
              onClick={() => handleCopy('phone', phone)}
              title={copiedKey === 'phone' ? 'Copiado' : 'Copiar celular'}
              aria-label={copiedKey === 'phone' ? 'Celular copiado' : `Copiar celular de ${name}`}
            >
              {copiedKey === 'phone' ? (
                <Check size={14} strokeWidth={2.25} aria-hidden />
              ) : (
                <Copy size={14} strokeWidth={2} aria-hidden />
              )}
            </button>
          ) : null}
        </div>

        <div className="passenger-card__addresses-block">
          <div className="passenger-card__addresses">
            <span className="passenger-card__addresses-icon" aria-hidden>
              <MapPin size={14} strokeWidth={2} />
            </span>
            {addresses.length > 0 ? (
              <ul className="passenger-card__address-list">
                {addresses.map((street, i) => (
                  <li key={`${street}-${i}`} className="passenger-card__address-item">
                    <span className="passenger-card__address-text" title={street}>
                      {street}
                    </span>
                    <button
                      type="button"
                      className={`passenger-card__copy-btn${copiedKey === `addr-${i}` ? ' is-copied' : ''}`}
                      onClick={() => handleCopy(`addr-${i}`, street)}
                      title={copiedKey === `addr-${i}` ? 'Copiado' : 'Copiar dirección'}
                      aria-label={
                        copiedKey === `addr-${i}`
                          ? 'Dirección copiada'
                          : `Copiar dirección: ${street}`
                      }
                    >
                      {copiedKey === `addr-${i}` ? (
                        <Check size={14} strokeWidth={2.25} aria-hidden />
                      ) : (
                        <Copy size={14} strokeWidth={2} aria-hidden />
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <span className="passenger-card__address-empty">Sin dirección</span>
            )}
          </div>
        </div>
      </div>
    </article>
  );
};

export default PassengerCard;
